/* eslint-disable max-len */
import moment from 'moment';

export default {
    name: 'OperationAddSelectOperation',
    props: ['newOperationState'],
    /**
     * @override
     */
    data() {
        return {
            accountProducts: null,
            isPensionPlansAllowed: false
        };
    },
    computed: {
        /**
         * @return {Boolean} if can do PP operation
         */
        canOperatePP() {
            let canOperate = true;
            if (this.newOperationState.isEdition) {
                canOperate = false;
            } else {
                let countTitulares = 0;
                let noSpanish = false;
                this.newOperationState.account.interveners.forEach(intervener => {
                    // Si hay algun titular (o nudo propietario) que sea persona jurídica, no puede operar para PP
                    // El resto se cuentan (si hay más de uno tampoco podrá operar)
                    intervener.account_roles.forEach(role => {
                        if (['TITULAR', 'NUDO_PROPIETARIO'].includes(role)) {
                            countTitulares++;
                            if (intervener.intervener_type === 'J') {
                                // Si es titular y persona jurídica, no puede operar
                                canOperate = false;
                            }
                            if (intervener.key_nationality_1 !== 'ESP') {
                                noSpanish = true;
                            }
                            if (this.isMinor(intervener)) {
                                // Si es menor no se puede operar PP
                                canOperate = false;
                            }
                        }
                    });
                });
                if (countTitulares > 1) {
                    // Si la cuenta de titulares es mayor que uno, tampoco podría operar
                    canOperate = false;
                } else if (countTitulares === 1 && noSpanish) {
                    canOperate = false;
                }
            }
            return canOperate;
        }, 
        /**
         * @return {Object} operation types availables
         */
        operationTypeOptions() {
            let options = [];
            if (this.newOperationState.productType.type) {
                options = this.newOperationState.operationTypeList.filter(operationType => {
                    return operationType.prt_key_name === this.newOperationState.productType.type;
                });
            }
            // eliminar "SOLICITUD_TRASPASO_EXTERNO_ENTRADA" "SOLICITUD_TRASPASO_INTERNO" si es una sociedad, persona jurídica o no residente en España
            if (
                this.newOperationState.ordering.intervener_type === 'J'
                || this.newOperationState.account.account_type === 'J'
                || (this.newOperationState.ordering && this.newOperationState.ordering.uuid && !this.spainResident(this.newOperationState.ordering.uuid))
            ) {
                options = this.removeTransferOperations(options);
            }

            // eliminar "SOLICITUD_SUSCRIPCION_PERIODICA" si ALLOW_PERIODIC_SUBSCRIPTIONS es false
            const disablePeriodicSubscriptions = !this.$store.getters['configVarsStore/getAllowPeriodicSubscriptions'];

            if (disablePeriodicSubscriptions) {
                options = options.filter(option => option.key_name !== 'SOLICITUD_SUSCRIPCION_PERIODICA');
                options = options.filter(option => option.key_name !== 'SOLICITUD_REEMBOLSO_PERIODICO');
            }

            options = this.checkIsTransfersBlocked(options);
            if (this.orderingIsFamily) {
                options = options.filter( option => option.key_name !== 'RESCATE');
            }
            return options;
        },
        orderingIsFamily() {
            const roles = this.newOperationState.ordering ? this.newOperationState.ordering.account_roles : undefined;
            if (!roles) {
                return false;
            }
            let result = false;
            roles.forEach( role => {
                if (role === 'APORTANTE_CONYUGE') {
                    result = true;
                }
            });
            return result;
        },
        /**
         * @return {Object} product options
         */
        productOptions() {
            let options = [];
            if (this.newOperationState.productType.type) {
                options = this.newOperationState.productList.filter(product => {
                    return product.prt_key_name === this.newOperationState.productType.type;
                });
            }

            if (
                this.newOperationState.operationTypeSelected &&
                (this.newOperationState.operationTypeSelected.key_name === 'REEMBOLSO' ||
                    this.newOperationState.operationTypeSelected.key_name === 'RESCATE' ||
                    this.newOperationState.operationTypeSelected.key_name === 'SOLICITUD_TRASPASO_INTERNO' ||
                    this.newOperationState.operationTypeSelected.key_name === 'SOLICITUD_REEMBOLSO_PERIODICO' ||
                    this.newOperationState.operationTypeSelected.key_name === 'REEMBOLSO_PERIODICO')
            ) {
                // Si es una de estas operaciones filtrar solo los productos en los que haya saldo
                // Si aún no se han recuperado las positions, devolver la lista vacía que mostrará 'Obteniendo información'
                if (this.newOperationState.positionsLoaded) {
                    options = options.filter(product => {
                        return (
                            this.newOperationState.account.products.hasOwnProperty(product.key_name) &&
                            this.newOperationState.account.products[product.key_name].positions > 0
                        );
                    });
                } else {
                    options = [];
                }
            }

            return options;
        },
        productLabel() {
            const op = this.newOperationState.operationTypeSelected?.key_name

            if (op === 'SOLICITUD_TRASPASO_INTERNO' || op === 'TRASPASO_INTERNO') {
                return this.$t('operations.originFund')
            }

            if (op === 'SOLICITUD_TRASPASO_EXTERNO_ENTRADA') {
                return this.$t('operations.destinationFund')
            }

            return this.$t('operations.product')
        }
    },
    methods: {
        /**
         * @param {Object} typeObj object of type selected
         */
        selectProductType(typeObj) {
            // Si no hay ordenante seleccionado, no hacer nada
            if (!this.newOperationState.ordering || (typeObj.type === 'P' && !this.canOperatePP)) {
                return;
            }
            if (typeObj !== this.newOperationState.productType) {
                this.newOperationState.productSelected = null;
                this.newOperationState.operationTypeSelected = null;

                this.newOperationState.productType = typeObj;

                this.newOperationState.operationStep = 2;
            }
        },
        /**
         * Load allows in config 
         */
        allowPensionPlans() {
            this.isPensionPlansAllowed = this.$store.getters['configVarsStore/getAllowPensionPlans'];
            // Si no están permitidos los planes de pensiones, selecciona automáticamente "Fondo de inversión"
            if (!this.isPensionPlansAllowed) this.selectProductType({ type: 'F', text: 'Fondo de inversión' });
        },
        /**
         * @param {Object} operationType object selected
         */
        onSelectOperationType(operationType) {
            this.newOperationState.operationTypeSelected = operationType;
            this.newOperationState.operationStep = 3;
        },
        /**
         * @param {Object} product object selected
         */
        onSelectProduct(product) {
            this.newOperationState.productSelected = product;
            // Emitimos al padre para que pueda verificar si es una inversión sucesiva
            this.$emit('product-changed', product);
            // TODO: Mock... como no tenemos valores de los productos, le pongo a pincho que vale 100...
            if (!this.newOperationState.productSelected.net_asset_value) {
                this.newOperationState.productSelected.net_asset_value = 100;
                this.newOperationState.productSelected.net_asset_value_date = new Date();
            }
            this.newOperationState.operationStep = 4;
        },

        // filtramos las operaciones permitidas para una PERSONA JURÍDICA
        removeTransferOperations(options) {
            // operaciones no permitidas
            const operationsNotAllowed = ['SOLICITUD_TRASPASO_EXTERNO_ENTRADA', 'SOLICITUD_TRASPASO_INTERNO'];
            // array que guardará las operaciones permitidas
            const operationsAllowed = [];
            options.forEach(op => {
                if (!operationsNotAllowed.includes(op.key_name)) {
                    operationsAllowed.push(op);
                }
            });
            // devolvemos un array con las operaciones permitidas
            return operationsAllowed;
        },

        // comprobamos si la cuenta tiene bloqueadas 'Ind. bloqueo trasp. salida ext'
        checkIsTransfersBlocked(options) {
            // operaciones no permitidas
            const operationsNotAllowed = [];
            if (this.newOperationState.account.is_transfers_blocked) {
                operationsNotAllowed.push('SOLICITUD_TRASPASO_EXTERNO_ENTRADA');
                operationsNotAllowed.push('SOLICITUD_TRASPASO_INTERNO');
            }
            // array que guardará las operaciones permitidas
            const operationsAllowed = [];
            options.forEach(op => {
                if (!operationsNotAllowed.includes(op.key_name)) {
                    operationsAllowed.push(op);
                }
            });
            // devolvemos un array con las operaciones permitidas
            return operationsAllowed;
        },

        // Comprobamos si el interviniente reside en España con su dirección postal
        async spainResident(intervenerUUID) {
            const response = await this.$intervenersService.getIntervenersByUUIId(intervenerUUID);
            const postalAddress = response.data.intervener.fiscal_address;
            if (postalAddress.f_address_key_country === 'ESP') {
                return true;
            } else {
                return false;
            }
        },

        isMinor(intervener, now = moment()) {
            return now.diff(moment(intervener.birthdate), 'years') < 18 && intervener.intervener_type === 'F';
        },

        canOperate(intervener) {
            let result = intervener.can_operate;  // si puede operar directamente se retorna el valor
            if (!intervener.can_operate && this.newOperationState.productType.type === 'P') {
                // si no puede operar, pero es un plan de pensiones, se mira si es aportante familiar.
                intervener.account_roles.forEach( role => {
                    if (role === 'APORTANTE_CONYUGE') {
                        result = true;
                    }
                });
            }
            return result;
        },

        intervenersOption(intervener) {
            const interveners = this.newOperationState.account.interveners || [];
            // Caso especial: SOLO UNO y es J → mostrar
            if (interveners.length === 1 && intervener.intervener_type === 'J') return true;            
            // Caso general: NO mostrar si es J
            return intervener.intervener_type !== 'J';
        }
    },
    /**
     * @override
     */
    async mounted() {
        this.allowPensionPlans()
        if (this.$route.query.uuid) {
            try {
                if (this.$route.query.type === 'periodic' && !this.newOperationState.periodicInfoLoaded) {
                    const res = await this.$operationsService.getOperationsByUUId(this.$route.query.uuid, {});
                    this.selectProductType({ type: 'F', text: 'Fondo de inversión' });
                    const ordering = this.newOperationState.account.interveners.find(intervener => intervener.uuid === res.data.operation.payer.uuid);
                    this.newOperationState.ordering = ordering;

                    const operationType = res.data.operation.key_operation_type.includes('SUSCRIPCION') ? 'SUSCRIPCION' : 'REEMBOLSO';
                    const selectedOperation = this.operationTypeOptions.find(option => option.key_name === operationType);
                    this.onSelectOperationType(selectedOperation);

                    let selectedProduct = this.productOptions.find(option => option.key_name === res.data.operation.key_product);

                    if (!selectedProduct) {
                        // Si no hay producto seleccionado, algo pasa... es posible que la clase sea diferente?
                        // para evitar el fallo, lo metemos directamente como seleccionado
                        // siempre se podría volver atrás para seleccionar otro producto
                        selectedProduct = this.newOperationState.productList.find(product => {
                            return product.key_name === res.data.operation.key_product;
                        });
                    }

                    this.onSelectProduct(selectedProduct);
                    this.newOperationState.source = 'ONLINE';
                    this.newOperationState.operationObj.grossAmount = Number(res.data.operation.gross_amount);

                    this.newOperationState.periodicInfoLoaded = true;
                }
            } catch (error) {
                console.error(error);
            }
        }
    }
};
